import { assetPath } from './appPaths';

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
const XLINK_NAMESPACE = 'http://www.w3.org/1999/xlink';
const DEFAULT_BACKGROUND_COLOR = '#ffffff';
const DEFAULT_TARGET_MAX_SIDE = 2400;
const MAX_EXPORT_SCALE = 3;
const DEFAULT_CONTENT_PADDING_RATIO = 0.012;
const MIN_CONTENT_PADDING = 4;
const MAX_CONTENT_PADDING = 32;
const DEFAULT_LOGO_SRC = assetPath('/logo.png');
const DEFAULT_LOGO_MARGIN = 18;
const DEFAULT_LOGO_WIDTH_RATIO = 0.16;
const MIN_LOGO_WIDTH = 96;
const MAX_LOGO_WIDTH = 220;
const DEFAULT_LOGO_IMAGE_OPACITY = 1;
const DEFAULT_LOGO_PLATE_OPACITY = 0.9;
const DEFAULT_LOGO_SHADOW_OPACITY = 0.12;
const DEFAULT_DOWNLOADED_LOGO_IMAGE_OPACITY = 0.42;
const DEFAULT_DOWNLOADED_LOGO_PLATE_OPACITY = 0.4;
const DEFAULT_DOWNLOADED_LOGO_SHADOW_OPACITY = 0.06;

const parsePositiveNumber = (value) => {
  const rawValue = String(value ?? '').trim();
  if (!rawValue || rawValue.endsWith('%')) {
    return null;
  }

  const parsedValue = Number.parseFloat(rawValue);
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
};

const normalizeViewBox = (value) => {
  if (!value) {
    return null;
  }

  const minX = Number(value.minX);
  const minY = Number(value.minY);
  const width = Number(value.width);
  const height = Number(value.height);

  if (!Number.isFinite(minX) || !Number.isFinite(minY) || !Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0) {
    return null;
  }

  return {
    minX,
    minY,
    width,
    height,
  };
};

const expandViewBox = (viewBox, padding = 0) => {
  const normalizedViewBox = normalizeViewBox(viewBox);
  const numericPadding = Number(padding);

  if (!normalizedViewBox) {
    return null;
  }

  if (!Number.isFinite(numericPadding) || numericPadding <= 0) {
    return normalizedViewBox;
  }

  return {
    minX: normalizedViewBox.minX - numericPadding,
    minY: normalizedViewBox.minY - numericPadding,
    width: normalizedViewBox.width + (numericPadding * 2),
    height: normalizedViewBox.height + (numericPadding * 2),
  };
};

const unionViewBoxes = (left, right) => {
  const normalizedLeft = normalizeViewBox(left);
  const normalizedRight = normalizeViewBox(right);

  if (!normalizedLeft) {
    return normalizedRight;
  }

  if (!normalizedRight) {
    return normalizedLeft;
  }

  const minX = Math.min(normalizedLeft.minX, normalizedRight.minX);
  const minY = Math.min(normalizedLeft.minY, normalizedRight.minY);
  const maxX = Math.max(normalizedLeft.minX + normalizedLeft.width, normalizedRight.minX + normalizedRight.width);
  const maxY = Math.max(normalizedLeft.minY + normalizedLeft.height, normalizedRight.minY + normalizedRight.height);

  return {
    minX,
    minY,
    width: maxX - minX,
    height: maxY - minY,
  };
};

const getSvgViewBox = (svgElement) => {
  if (!svgElement) {
    return null;
  }

  const liveViewBox = svgElement.viewBox?.baseVal;
  if (liveViewBox && Number.isFinite(liveViewBox.width) && liveViewBox.width > 0 && Number.isFinite(liveViewBox.height) && liveViewBox.height > 0) {
    return {
      minX: Number(liveViewBox.x) || 0,
      minY: Number(liveViewBox.y) || 0,
      width: Number(liveViewBox.width),
      height: Number(liveViewBox.height),
    };
  }

  const rawViewBox = String(svgElement.getAttribute('viewBox') || '').trim();
  if (!rawViewBox) {
    return null;
  }

  const parts = rawViewBox
    .split(/[\s,]+/)
    .map((token) => Number.parseFloat(token))
    .filter((token) => Number.isFinite(token));

  if (parts.length !== 4) {
    return null;
  }

  const [minX, minY, width, height] = parts;
  return normalizeViewBox({ minX, minY, width, height });
};

const getSvgGeometry = (svgElement) => {
  const viewBox = getSvgViewBox(svgElement);
  let width = parsePositiveNumber(svgElement?.getAttribute('width'));
  let height = parsePositiveNumber(svgElement?.getAttribute('height'));

  if ((!width || !height) && viewBox) {
    width = width || viewBox.width;
    height = height || viewBox.height;
  }

  if ((!width || !height) && typeof svgElement?.getBBox === 'function') {
    try {
      const bounds = svgElement.getBBox();
      if (bounds && Number.isFinite(bounds.width) && bounds.width > 0 && Number.isFinite(bounds.height) && bounds.height > 0) {
        width = width || bounds.width;
        height = height || bounds.height;

        if (!viewBox) {
          return {
            width,
            height,
            viewBox: {
              minX: Number(bounds.x) || 0,
              minY: Number(bounds.y) || 0,
              width: bounds.width,
              height: bounds.height,
            },
          };
        }
      }
    } catch (error) {
      // Ignore measurement failures and fall back to layout bounds.
    }
  }

  if ((!width || !height) && typeof svgElement?.getBoundingClientRect === 'function') {
    const rect = svgElement.getBoundingClientRect();
    if (rect && Number.isFinite(rect.width) && rect.width > 0 && Number.isFinite(rect.height) && rect.height > 0) {
      width = width || rect.width;
      height = height || rect.height;
    }
  }

  const normalizedWidth = width || viewBox?.width || 1;
  const normalizedHeight = height || viewBox?.height || 1;

  return {
    width: normalizedWidth,
    height: normalizedHeight,
    viewBox: viewBox || {
      minX: 0,
      minY: 0,
      width: normalizedWidth,
      height: normalizedHeight,
    },
  };
};

const createMeasurementHost = () => {
  const host = document.createElement('div');
  host.setAttribute('aria-hidden', 'true');
  host.style.position = 'fixed';
  host.style.left = '-100000px';
  host.style.top = '-100000px';
  host.style.opacity = '0';
  host.style.pointerEvents = 'none';
  host.style.overflow = 'hidden';
  host.style.visibility = 'hidden';
  return host;
};

const getContentPadding = (viewBox) => {
  const normalizedViewBox = normalizeViewBox(viewBox);
  if (!normalizedViewBox) {
    return MIN_CONTENT_PADDING;
  }

  const longestSide = Math.max(normalizedViewBox.width, normalizedViewBox.height);
  const paddedValue = longestSide * DEFAULT_CONTENT_PADDING_RATIO;
  return Math.max(MIN_CONTENT_PADDING, Math.min(MAX_CONTENT_PADDING, Number(paddedValue.toFixed(2))));
};

const measureSvgContentBounds = (svgElement, geometry) => {
  if (!(svgElement instanceof SVGElement) || typeof document === 'undefined' || !document.body) {
    return null;
  }

  const measurementHost = createMeasurementHost();
  const measurementSvg = svgElement.cloneNode(true);

  measurementSvg.setAttribute('xmlns', SVG_NAMESPACE);
  measurementSvg.setAttribute('xmlns:xlink', XLINK_NAMESPACE);
  measurementSvg.setAttribute('width', String(geometry.width));
  measurementSvg.setAttribute('height', String(geometry.height));
  measurementSvg.setAttribute('viewBox', `${geometry.viewBox.minX} ${geometry.viewBox.minY} ${geometry.viewBox.width} ${geometry.viewBox.height}`);

  if (measurementSvg.style) {
    measurementSvg.style.setProperty('width', `${geometry.width}px`, 'important');
    measurementSvg.style.setProperty('height', `${geometry.height}px`, 'important');
    measurementSvg.style.setProperty('overflow', 'visible', 'important');
    measurementSvg.style.removeProperty('max-width');
    measurementSvg.style.removeProperty('max-height');
    measurementSvg.style.removeProperty('object-fit');
    measurementSvg.style.removeProperty('transform');
    measurementSvg.style.removeProperty('transform-origin');
  }

  sanitizeHiddenElements(measurementSvg);

  const measureGroup = document.createElementNS(SVG_NAMESPACE, 'g');
  const preserveTags = new Set(['defs', 'style', 'title', 'desc', 'metadata']);

  Array.from(measurementSvg.childNodes).forEach((child) => {
    if (child.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    if (preserveTags.has(child.nodeName.toLowerCase())) {
      return;
    }

    measureGroup.appendChild(child.cloneNode(true));
  });

  if (!measureGroup.childNodes.length) {
    return null;
  }

  measurementSvg.appendChild(measureGroup);
  measurementHost.appendChild(measurementSvg);
  document.body.appendChild(measurementHost);

  try {
    const box = measureGroup.getBBox();
    if (!Number.isFinite(box.x) || !Number.isFinite(box.y) || !Number.isFinite(box.width) || box.width <= 0 || !Number.isFinite(box.height) || box.height <= 0) {
      return null;
    }

    return {
      minX: Number(box.x.toFixed(2)),
      minY: Number(box.y.toFixed(2)),
      width: Number(box.width.toFixed(2)),
      height: Number(box.height.toFixed(2)),
    };
  } catch (error) {
    return null;
  } finally {
    measurementHost.remove();
  }
};

const insertSvgBackground = (svgElement, viewBox, backgroundColor) => {
  const color = String(backgroundColor || '').trim();
  if (!color) {
    return;
  }

  const backgroundRect = svgElement.ownerDocument.createElementNS(SVG_NAMESPACE, 'rect');
  backgroundRect.setAttribute('x', String(viewBox.minX));
  backgroundRect.setAttribute('y', String(viewBox.minY));
  backgroundRect.setAttribute('width', String(viewBox.width));
  backgroundRect.setAttribute('height', String(viewBox.height));
  backgroundRect.setAttribute('fill', color);
  backgroundRect.setAttribute('pointer-events', 'none');

  svgElement.insertBefore(backgroundRect, svgElement.firstChild);
};

const sanitizeHiddenElements = (svgElement) => {
  svgElement.querySelectorAll('.hidden').forEach((element) => {
    element.setAttribute('display', 'none');
    if (element.style) {
      element.style.setProperty('display', 'none', 'important');
    }
  });
};

const sanitizeFileName = (value, fallbackBaseName = 'diagram-export') => {
  const normalizeBaseName = (baseName) => String(baseName || '')
    .normalize('NFKD')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const rawValue = String(value || '').trim();
  const fallback = normalizeBaseName(fallbackBaseName) || 'diagram-export';
  const withoutExtension = rawValue.replace(/\.png$/i, '');
  const baseName = normalizeBaseName(withoutExtension) || fallback;

  return `${baseName}.png`;
};

const loadImage = (url) => new Promise((resolve, reject) => {
  const image = new Image();
  image.crossOrigin = 'anonymous';
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error('Unable to load SVG image for export.'));
  image.src = url;
});

const loadOptionalImage = async (url) => {
  const normalizedUrl = String(url || '').trim();
  if (!normalizedUrl) {
    return null;
  }

  try {
    return await loadImage(normalizedUrl);
  } catch (error) {
    return null;
  }
};

const clampOpacity = (value, fallback) => {
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) {
    return fallback;
  }

  return Math.max(0, Math.min(1, parsedValue));
};

const drawRoundedRect = (context, x, y, width, height, radius) => {
  const safeRadius = Math.max(0, Math.min(radius, width / 2, height / 2));

  context.beginPath();
  context.moveTo(x + safeRadius, y);
  context.lineTo(x + width - safeRadius, y);
  context.quadraticCurveTo(x + width, y, x + width, y + safeRadius);
  context.lineTo(x + width, y + height - safeRadius);
  context.quadraticCurveTo(x + width, y + height, x + width - safeRadius, y + height);
  context.lineTo(x + safeRadius, y + height);
  context.quadraticCurveTo(x, y + height, x, y + height - safeRadius);
  context.lineTo(x, y + safeRadius);
  context.quadraticCurveTo(x, y, x + safeRadius, y);
  context.closePath();
};

const drawLogoOverlay = async (context, canvasWidth, canvasHeight, options = {}) => {
  if (options.includeLogoOverlay === false) {
    return;
  }

  const logoImage = await loadOptionalImage(options.logoSrc || DEFAULT_LOGO_SRC);
  if (!logoImage?.naturalWidth || !logoImage?.naturalHeight) {
    return;
  }

  const numericMargin = parsePositiveNumber(options.logoMargin);
  const margin = numericMargin || DEFAULT_LOGO_MARGIN;
  const maxAvailableWidth = Math.max(0, canvasWidth - (margin * 2));
  const desiredWidth = Math.round(canvasWidth * DEFAULT_LOGO_WIDTH_RATIO);
  const logoWidth = Math.min(
    maxAvailableWidth,
    Math.max(MIN_LOGO_WIDTH, Math.min(MAX_LOGO_WIDTH, desiredWidth))
  );

  if (!Number.isFinite(logoWidth) || logoWidth <= 0) {
    return;
  }

  const logoHeight = Math.round((logoWidth / logoImage.naturalWidth) * logoImage.naturalHeight);
  const maxAvailableHeight = Math.max(0, canvasHeight - (margin * 2));
  if (!Number.isFinite(logoHeight) || logoHeight <= 0 || logoHeight > maxAvailableHeight) {
    return;
  }

  const x = canvasWidth - margin - logoWidth;
  const y = canvasHeight - margin - logoHeight;
  const platePadding = Math.max(8, Math.round(logoWidth * 0.045));
  const plateRadius = Math.max(10, Math.round(logoHeight * 0.22));
  const plateOpacity = clampOpacity(options.logoPlateOpacity, DEFAULT_LOGO_PLATE_OPACITY);
  const logoOpacity = clampOpacity(options.logoOpacity, DEFAULT_LOGO_IMAGE_OPACITY);
  const shadowOpacity = clampOpacity(options.logoShadowOpacity, DEFAULT_LOGO_SHADOW_OPACITY);

  context.save();
  context.fillStyle = `rgba(255, 255, 255, ${plateOpacity})`;
  context.shadowColor = `rgba(15, 23, 42, ${shadowOpacity})`;
  context.shadowBlur = Math.max(8, Math.round(logoWidth * 0.08));
  context.shadowOffsetY = Math.max(2, Math.round(logoHeight * 0.04));
  drawRoundedRect(
    context,
    x - platePadding,
    y - platePadding,
    logoWidth + (platePadding * 2),
    logoHeight + (platePadding * 2),
    plateRadius
  );
  context.fill();
  context.restore();

  context.save();
  context.globalAlpha = logoOpacity;
  context.drawImage(logoImage, x, y, logoWidth, logoHeight);
  context.restore();
};

const renderSvgElementAsPngDataUrl = async (svgElement, options = {}) => {
  if (!(svgElement instanceof SVGElement)) {
    throw new Error('A rendered SVG element is required for PNG export.');
  }

  const baseGeometry = getSvgGeometry(svgElement);
  const clone = svgElement.cloneNode(true);

  const explicitViewBox = normalizeViewBox(options.viewBox);
  const shouldExpandToVisibleContent = explicitViewBox === null && options.expandToVisibleContent !== false;

  if (typeof options.prepareClone === 'function') {
    options.prepareClone(clone, {
      width: baseGeometry.width,
      height: baseGeometry.height,
      viewBox: baseGeometry.viewBox,
    });
  }

  sanitizeHiddenElements(clone);

  const contentBounds = shouldExpandToVisibleContent
    ? measureSvgContentBounds(clone, baseGeometry)
    : null;
  const expandedContentBounds = contentBounds
    ? expandViewBox(contentBounds, getContentPadding(contentBounds))
    : null;
  const outputViewBox = explicitViewBox
    || unionViewBoxes(baseGeometry.viewBox, expandedContentBounds)
    || baseGeometry.viewBox;
  const outputWidth = parsePositiveNumber(options.outputWidth) || outputViewBox.width || baseGeometry.width;
  const outputHeight = parsePositiveNumber(options.outputHeight) || outputViewBox.height || baseGeometry.height;
  const targetMaxSide = parsePositiveNumber(options.targetMaxSide) || DEFAULT_TARGET_MAX_SIDE;
  const backgroundColor = options.backgroundColor === undefined ? DEFAULT_BACKGROUND_COLOR : options.backgroundColor;
  const fileName = sanitizeFileName(options.fileName, options.fallbackFileName);

  clone.setAttribute('xmlns', SVG_NAMESPACE);
  clone.setAttribute('xmlns:xlink', XLINK_NAMESPACE);
  clone.setAttribute('width', String(outputWidth));
  clone.setAttribute('height', String(outputHeight));
  clone.setAttribute('viewBox', `${outputViewBox.minX} ${outputViewBox.minY} ${outputViewBox.width} ${outputViewBox.height}`);
  clone.removeAttribute('aria-hidden');

  if (clone.style) {
    clone.style.setProperty('width', `${outputWidth}px`, 'important');
    clone.style.setProperty('height', `${outputHeight}px`, 'important');
    clone.style.removeProperty('max-width');
    clone.style.removeProperty('max-height');
    clone.style.removeProperty('object-fit');
    clone.style.removeProperty('transform');
    clone.style.removeProperty('transform-origin');
  }

  insertSvgBackground(clone, outputViewBox, backgroundColor);

  const serializedSvg = new XMLSerializer().serializeToString(clone);
  const svgUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(serializedSvg)}`;
  const longestSide = Math.max(outputWidth, outputHeight);
  const exportScale = longestSide > 0
    ? Math.max(1, Math.min(MAX_EXPORT_SCALE, targetMaxSide / longestSide))
    : 1;
  const canvasWidth = Math.max(1, Math.round(outputWidth * exportScale));
  const canvasHeight = Math.max(1, Math.round(outputHeight * exportScale));

  const image = await loadImage(svgUrl);
  const canvas = document.createElement('canvas');
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;

  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Unable to create a drawing context for PNG export.');
  }

  context.imageSmoothingEnabled = true;

  if (backgroundColor) {
    context.fillStyle = backgroundColor;
    context.fillRect(0, 0, canvasWidth, canvasHeight);
  } else {
    context.clearRect(0, 0, canvasWidth, canvasHeight);
  }

  context.drawImage(image, 0, 0, canvasWidth, canvasHeight);
  await drawLogoOverlay(context, canvasWidth, canvasHeight, options);

  const outputMimeType = options.outputMimeType || 'image/png';
  const outputQuality = Number.isFinite(Number(options.outputQuality))
    ? Number(options.outputQuality)
    : undefined;

  return {
    dataUrl: canvas.toDataURL(outputMimeType, outputQuality),
    fileName,
  };
};

export const createSvgElementPngDataUrl = async (svgElement, options = {}) => {
  const renderedImage = await renderSvgElementAsPngDataUrl(svgElement, options);
  return renderedImage.dataUrl;
};

export const createSvgElementJpegDataUrl = async (svgElement, options = {}) => {
  const renderedImage = await renderSvgElementAsPngDataUrl(svgElement, {
    ...options,
    backgroundColor: options.backgroundColor ?? DEFAULT_BACKGROUND_COLOR,
    outputMimeType: 'image/jpeg',
    outputQuality: options.outputQuality ?? 0.82,
  });
  return renderedImage.dataUrl;
};

export const downloadSvgElementAsPng = async (svgElement, options = {}) => {
  const renderedImage = await renderSvgElementAsPngDataUrl(svgElement, {
    ...options,
    logoOpacity: options.logoOpacity ?? DEFAULT_DOWNLOADED_LOGO_IMAGE_OPACITY,
    logoPlateOpacity: options.logoPlateOpacity ?? DEFAULT_DOWNLOADED_LOGO_PLATE_OPACITY,
    logoShadowOpacity: options.logoShadowOpacity ?? DEFAULT_DOWNLOADED_LOGO_SHADOW_OPACITY,
  });
  const link = document.createElement('a');
  link.href = renderedImage.dataUrl;
  link.download = renderedImage.fileName;
  link.click();
};
